import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../hooks/useBrandingColors';
import {
  companiesManagementService,
  kopToRubSmart,
  formatUnitsCompact,
  formatPricePerUnit,
  STATUS_LABELS,
  STATUS_COLORS,
  type Company,
  type CompanyModuleAnalytics,
} from '../services/companiesManagementService';
import { CreateCompanyModal } from './companies/CreateCompanyModal';
import { EditCompanyModal } from './companies/EditCompanyModal';
import { TopUpBalanceModal } from './companies/TopUpBalanceModal';
import { CompanyCardContent } from './companies/CompanyCardContent';

// ─── Icons ────────────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <path d="M10 4.75V15.25M4.75 10H15.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <circle cx="9" cy="9" r="5.5" stroke="#525866" strokeWidth="1.5"/>
    <path d="M13.5 13.5L17 17" stroke="#525866" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <rect x="2.5" y="4" width="15" height="13.5" rx="1.5" stroke="#525866" strokeWidth="1.4"/>
    <path d="M6.5 2v3M13.5 2v3M2.5 8h15" stroke="#525866" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <path d="M10 3.5v9.5M6.5 9.5l3.5 3.5 3.5-3.5" stroke="#525866" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.5 16.5h13" stroke="#525866" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <path d="M6 8.5L10 12.5L14 8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// coin-line (монета) — по макету
const BalanceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <ellipse cx="8" cy="5.3" rx="5.5" ry="2.6" stroke="#161922" strokeWidth="1.3"/>
    <path d="M2.5 5.3v3.1c0 1.44 2.46 2.6 5.5 2.6s5.5-1.16 5.5-2.6V5.3" stroke="#161922" strokeWidth="1.3"/>
  </svg>
);

// line-chart-line (линейный график с осями) — по макету
const ChartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2.2 2v12h11.6" stroke="#161922" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4.5 10.5L7 7.5L9.3 9.4L13.3 4.7" stroke="#161922" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserCountIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="#989286" className="shrink-0">
    <circle cx="7" cy="4.2" r="2.4"/>
    <path d="M2.2 11.5c0-2.4 2.1-3.8 4.8-3.8s4.8 1.4 4.8 3.8v.3H2.2v-.3Z"/>
  </svg>
);

const MoreIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#0A0D14" className="shrink-0">
    <circle cx="12" cy="5.5" r="1.6"/>
    <circle cx="12" cy="12" r="1.6"/>
    <circle cx="12" cy="18.5" r="1.6"/>
  </svg>
);

// ─── Types ────────────────────────────────────────────────────────────────────

type ActiveTab = 'usage' | 'revenue';

/** Маржа в % = 100·(выручка − себестоимость)/выручка. 0, если выручки нет (REL-248). */
const marginPctOf = (revenueKopecks: number, costKopecks: number): number =>
  revenueKopecks > 0 ? ((revenueKopecks - costKopecks) / revenueKopecks) * 100 : 0;

/** Маржа для подписи: целые проценты как в макете («107%»). */
const formatMarginPct = (pct: number): string => `${Math.round(pct)}%`;

/**
 * Подсветка найденного: красит брендовым #E16349 ТОЛЬКО совпавшую подстроку запроса в имени
 * (а не всё имя). Регистронезависимо, по всем вхождениям; исходный регистр сохраняется.
 */
export const highlightMatch = (text: string, query: string): React.ReactNode => {
  const q = query.trim();
  if (!q) return text;
  const lower = text.toLowerCase();
  const lq = q.toLowerCase();
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < text.length) {
    const idx = lower.indexOf(lq, i);
    if (idx === -1) {
      out.push(text.slice(i));
      break;
    }
    if (idx > i) out.push(text.slice(i, idx));
    out.push(
      <span key={k++} className="text-[#E16349]">{text.slice(idx, idx + lq.length)}</span>,
    );
    i = idx + lq.length;
  }
  return out;
};

/** ISO "2026-03-08" → "08.03.2026" (формат дат как в макете). Прочее возвращаем как есть. */
const formatRuDate = (iso?: string | null): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

// ─── Stat Widget ──────────────────────────────────────────────────────────────

interface StatWidgetProps {
  icon: React.ReactNode;
  label: string;
  /** Сумма в копейках. Целая часть — крупно (#161922), копейки и ₽ — мельче и серым (#989286). */
  kopecks: number;
}
const StatWidget: React.FC<StatWidgetProps> = ({ icon, label, kopecks }) => {
  const intPart = Math.trunc(kopecks / 100).toLocaleString('ru-RU');
  const frac = String(Math.abs(kopecks % 100)).padStart(2, '0');
  return (
    <div className="flex-1 min-w-0 h-[172px] bg-white border border-[#F4F5F0] rounded-[16px] p-5 flex flex-col justify-between gap-4">
      {/* Иконка в боксе 26×26 + заголовок */}
      <div className="flex items-center gap-[10px]">
        <span className="flex items-center justify-center w-[26px] h-[26px] bg-[#F4F5F0] rounded-[6px] shrink-0">
          {icon}
        </span>
        <span className="text-[14px] font-semibold text-[#161922] tracking-[-0.15px]">{label}</span>
      </div>
      {/* Число целиком 36px тёмное (с копейками), только «₽» 24px серым (по макету) */}
      <div className="flex items-baseline gap-1.5">
        <span className="text-[36px] font-semibold leading-[1.3] tracking-[0.07px] text-[#161922]">{intPart},{frac}</span>
        <span className="text-[24px] font-semibold leading-[1.3] text-[#989286]">₽</span>
      </div>
    </div>
  );
};

// ─── Period Picker ────────────────────────────────────────────────────────────

interface PeriodPickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
}

// Пресеты периода по макету «ВЫБЕРИТЕ ПЕРИОД». Порядок — как в сетке 2 колонки (по строкам):
// Сегодня | За неделю / Вчера | За месяц. Бэкенд фильтрует по (dateFrom,dateTo), поэтому пресет —
// это просто вычисленная пара дат; произвольный период вводится в полях От/До (ручной ввод + календарь).
type PeriodMode = 'today' | 'week' | 'yesterday' | 'month' | 'custom' | null;
const PERIOD_PRESETS: { key: Exclude<PeriodMode, 'custom' | null>; label: string }[] = [
  { key: 'today', label: 'Сегодня' },
  { key: 'week', label: 'За неделю' },
  { key: 'yesterday', label: 'Вчера' },
  { key: 'month', label: 'За месяц' },
];

const pad2 = (n: number) => String(n).padStart(2, '0');
// Локальная (не UTC) ISO-дата YYYY-MM-DD — чтобы «Сегодня» не уезжало на день в часовых поясах > UTC.
const isoLocal = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const PeriodPicker: React.FC<PeriodPickerProps> = ({ dateFrom, dateTo, onChange }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<PeriodMode>(null);
  const [localFrom, setLocalFrom] = useState(dateFrom);
  const [localTo, setLocalTo] = useState(dateTo);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Диапазон дат для пресета (включительно). Неделя/месяц — скользящие 7/30 дней по сегодня.
  const rangeOf = (key: Exclude<PeriodMode, 'custom' | null>): [string, string] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const shift = (days: number) => {
      const d = new Date(today);
      d.setDate(today.getDate() + days);
      return d;
    };
    switch (key) {
      case 'today': return [isoLocal(today), isoLocal(today)];
      case 'yesterday': return [isoLocal(shift(-1)), isoLocal(shift(-1))];
      case 'week': return [isoLocal(shift(-6)), isoLocal(today)];
      case 'month': return [isoLocal(shift(-29)), isoLocal(today)];
    }
  };

  const pickPreset = (key: Exclude<PeriodMode, 'custom' | null>) => {
    const [from, to] = rangeOf(key);
    setMode(key);
    setLocalFrom(from);
    setLocalTo(to);
    onChange(from, to);
    setOpen(false);
  };

  // Произвольный период: открываем поля От/До, инициализируем текущими/сегодняшней датой.
  const enableCustom = () => {
    setMode('custom');
    const fallback = isoLocal(new Date());
    if (!localFrom) setLocalFrom(dateFrom || fallback);
    if (!localTo) setLocalTo(dateTo || fallback);
  };
  // Применяем диапазон, как только обе даты заданы (и от ≤ до).
  const applyCustom = (from: string, to: string) => {
    if (from && to && from <= to) onChange(from, to);
  };

  // Компактный формат «05.06» из ISO «2026-06-05» — узкий и стабильный лейбл; полная дата с годом в тултипе.
  const fmtShort = (iso: string): string => {
    const [, m, d] = iso.split('-');
    return d && m ? `${d}.${m}` : iso;
  };
  const presetLabel = PERIOD_PRESETS.find(p => p.key === mode)?.label;
  const label = presetLabel
    ? presetLabel
    : dateFrom && dateTo
      ? `${fmtShort(dateFrom)} – ${fmtShort(dateTo)}`
      : 'Период';
  const fullTitle = dateFrom && dateTo ? `${dateFrom} — ${dateTo}` : 'Выбрать период';

  // Классы кнопки-пресета: выбранный — оранжевый фон; остальные — белые с рамкой (по макету).
  const presetCls = (active: boolean) =>
    `flex items-center justify-center h-10 rounded-[10px] text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors ${
      active
        ? 'bg-[#E16349] text-white border border-[#E16349]'
        : 'bg-white text-[#0A0D14] border border-[#E2E4E9] hover:bg-[#f8f9fa] shadow-[0px_1px_2px_rgba(55,93,251,0.08)]'
    }`;

  return (
    <div ref={ref} className="relative">
      {/* Фиксированная ширина кнопки (REL-228 баг 2): лейбл периода не меняет ширину при выборе,
          поэтому правый кластер тулбара (вкл. «Добавить компанию») не «прыгает». */}
      <button
        onClick={() => setOpen(v => !v)}
        title={fullTitle}
        className="flex items-center gap-1 w-[158px] bg-white rounded-[10px] px-2.5 py-2.5 text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#525866] shadow-[0px_1px_2px_rgba(55,93,251,0.08)] hover:bg-[#f8f9fa] transition-colors"
      >
        <CalendarIcon />
        <span className="flex-1 min-w-0 truncate text-left">{label}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-[#E2E4E9] rounded-[16px] shadow-[0px_16px_32px_-12px_rgba(88,92,95,0.1)] z-50 p-4 w-[303px] flex flex-col gap-3">
          <span className="text-[12px] font-medium leading-6 tracking-[-0.011em] text-[#525866]">ВЫБЕРИТЕ ПЕРИОД</span>

          {/* Сетка пресетов 2×2 */}
          <div className="grid grid-cols-2 gap-2">
            {PERIOD_PRESETS.map(p => (
              <button key={p.key} type="button" onClick={() => pickPreset(p.key)} className={presetCls(mode === p.key)}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Произвольный период */}
          <button
            type="button"
            onClick={enableCustom}
            className={`flex items-center justify-center h-10 rounded-[10px] text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors ${
              mode === 'custom'
                ? 'bg-[rgba(225,99,73,0.1)] text-[#E16349] border border-[#E16349]'
                : 'bg-white text-[#0A0D14] border border-[#E2E4E9] hover:bg-[#f8f9fa] shadow-[0px_1px_2px_rgba(55,93,251,0.08)]'
            }`}
          >
            Произвольный период
          </button>

          {/* Поля От/До — нативный date (ручной ввод + календарь). Показываем в режиме «Произвольный». */}
          {mode === 'custom' && (
            <div className="flex items-end gap-1.5">
              <label className="flex-1 flex flex-col gap-1">
                <span className="text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#868C98]">От</span>
                <input
                  type="date"
                  value={localFrom}
                  max={localTo || undefined}
                  onChange={e => { setLocalFrom(e.target.value); applyCustom(e.target.value, localTo); }}
                  className="h-10 px-3 border border-[#E16349] rounded-[10px] text-[14px] leading-5 text-[#0A0D14] outline-none shadow-[0px_1px_2px_rgba(228,229,231,0.24)]"
                />
              </label>
              <span className="mb-[18px] w-2 h-px bg-[#99A1AF] shrink-0" />
              <label className="flex-1 flex flex-col gap-1">
                <span className="text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#868C98]">До</span>
                <input
                  type="date"
                  value={localTo}
                  min={localFrom || undefined}
                  onChange={e => { setLocalTo(e.target.value); applyCustom(localFrom, e.target.value); }}
                  className="h-10 px-3 border border-[#E2E4E9] rounded-[10px] text-[14px] leading-5 text-[#0A0D14] outline-none shadow-[0px_1px_2px_rgba(228,229,231,0.24)] focus:border-[#E16349]"
                />
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Status Dot ───────────────────────────────────────────────────────────────

// Пилот — фиолетовый подарок 🎁 (REL-195), остальные статусы — цветная точка.
const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'PILOT') {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 shrink-0" title="На пилоте">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 6h12v2H2V6Zm1 2.5h10V14H3V8.5ZM8 4.5V14M8 4.5C8 3 6.8 2 5.5 2.5 4.5 2.9 4.8 4.2 6 4.5H8Zm0 0c0-1.5 1.2-2.5 2.5-2 1 .4.7 1.7-.5 2H8Z"
            stroke={STATUS_COLORS.PILOT} strokeWidth="1.2" strokeLinejoin="round"/>
        </svg>
      </span>
    );
  }
  // Точка 8px с тонкой обводкой rgba(0,0,0,0.2) внутри бокса 16px (по макету)
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 shrink-0">
      <span
        className="w-2 h-2 rounded-full border border-black/20"
        style={{ backgroundColor: STATUS_COLORS[status] ?? '#868c98' }}
      />
    </span>
  );
};

// ─── Status Multi-Select (REL-195) ────────────────────────────────────────────

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'ACTIVE', label: 'Активные' },
  { value: 'PILOT', label: 'Пилотные' },
  { value: 'SUSPENDED', label: 'На паузе' },
  { value: 'INACTIVE', label: 'Заблокированные' },
];

const StatusMultiSelect: React.FC<{
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const label = selected.length === 0 ? 'Все статусы' : `Выбранно ${selected.length}`;

  // Кластер индикаторов статусов (легенда) — по макету: точки активная/пауза/блок + 🎁 пилот.
  const dot = (color: string, overlap = true) => (
    <span
      className={`w-2.5 h-2.5 rounded-full border border-black/20 shadow-[0_0_0_2px_#FDFDFC] ${overlap ? '-ml-1.5' : ''}`}
      style={{ backgroundColor: color }}
    />
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 h-10 bg-[#FDFDFC] rounded-[10px] pl-3 pr-2.5 text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#525866] shadow-[0px_1px_2px_rgba(55,93,251,0.08)] hover:bg-[#f8f9fa] transition-colors"
      >
        <span className="whitespace-nowrap">{label}</span>
        <span className="flex items-center">
          {dot(STATUS_COLORS.ACTIVE, false)}
          {dot(STATUS_COLORS.SUSPENDED)}
          {dot(STATUS_COLORS.INACTIVE)}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="ml-1 shrink-0">
            <path d="M2 6h12v2H2V6Zm1 2.5h10V14H3V8.5ZM8 4.5V14M8 4.5C8 3 6.8 2 5.5 2.5 4.5 2.9 4.8 4.2 6 4.5H8Zm0 0c0-1.5 1.2-2.5 2.5-2 1 .4.7 1.7-.5 2H8Z"
              stroke={STATUS_COLORS.PILOT} strokeWidth="1.2" strokeLinejoin="round"/>
          </svg>
        </span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-[#F4F5F0] rounded-[10px] shadow-[0px_16px_32px_-12px_rgba(88,92,95,0.1)] z-50 p-2 min-w-[200px]">
          {STATUS_OPTIONS.map(o => (
            <label
              key={o.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded-[8px] hover:bg-[#f8f9fa] cursor-pointer text-[13px] text-[#0a0d14]"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="w-4 h-4 rounded cursor-pointer accent-[#e16349]"
              />
              <span className="inline-flex items-center gap-1.5">
                <StatusDot status={o.value} />
                {o.label}
              </span>
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full mt-1 py-1.5 text-[12px] text-[#868c98] hover:text-[#e16349] transition-colors"
            >
              Сбросить
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Row Actions — 4 явные иконки по макету ──────────────────────────────────

const TopUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M9 6v6M6 9h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const StatsBarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <rect x="2" y="10" width="3" height="6" rx="1" stroke="currentColor" strokeWidth="1.3"/>
    <rect x="7.5" y="6" width="3" height="10" rx="1" stroke="currentColor" strokeWidth="1.3"/>
    <rect x="13" y="2" width="3" height="14" rx="1" stroke="currentColor" strokeWidth="1.3"/>
  </svg>
);

const EditPenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M11 3.5L14.5 7L6 15.5H2.5V12L11 3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M3 5h12M7 5V3.5h4V5M6 5v9.5h6V5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

interface RowActionsProps {
  onTopUp: () => void;
  onStats: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

// По макету в строке одна иконка «⋮», раскрывающая меню действий.
const RowActions: React.FC<RowActionsProps> = ({ onTopUp, onStats, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Строка меню по макету тултипа: иконка тёмная (#161922) + лейбл #525866 14px,
  // строки разделены 1px #F4F5F0 (divide-y на контейнере), высота ≈44px (py-3 + 20px).
  const item = (label: string, icon: React.ReactNode, onClick: () => void, danger = false) => (
    <button
      type="button"
      onClick={() => { onClick(); setOpen(false); }}
      className={`flex items-center gap-3 w-full px-3 py-3 text-[14px] font-medium leading-5 transition-colors text-left ${
        danger ? 'text-[#DF1C41] hover:bg-[#fef2f2]' : 'text-[#525866] hover:bg-[#f8f9fa]'
      }`}
    >
      <span className={`shrink-0 ${danger ? '' : 'text-[#161922]'}`}>{icon}</span>
      {label}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Действия"
        className="flex items-center justify-center w-6 h-6 rounded-[6px] hover:bg-[#f0f0f0] transition-colors"
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-[#F4F5F0] rounded-[10px] shadow-[0px_16px_32px_-12px_rgba(88,92,95,0.1)] z-50 overflow-hidden min-w-[189px] divide-y divide-[#F4F5F0]">
          {item('Пополнить баланс', <TopUpIcon />, onTopUp)}
          {item('Статистика и лимиты', <StatsBarIcon />, onStats)}
          {item('Редактировать', <EditPenIcon />, onEdit)}
          {item('Удалить компанию', <TrashIcon />, onDelete, true)}
        </div>
      )}
    </div>
  );
};

// ─── Stats Cell ───────────────────────────────────────────────────────────────

interface StatsCellProps {
  amount: number;
  mode: ActiveTab;
  units?: number;
  /** REL-247: настроенная стоимость модуля (тариф клиента) ₽/шт для строки «48К шт. (1₽)». */
  tariffKopecks?: number;
  /** Маржа модуля в % для режима «Доход» (маржинальность по каждому модулю). */
  marginPct?: number | null;
}

// Применение: сумма + «48К шт. (1₽)» — где (1₽) это стоимость модуля (REL-247).
// Доход: сумма + «маржа 42%» — маржинальность по каждому модулю (зелёным).
const StatsCell: React.FC<StatsCellProps> = ({ amount, mode, units, tariffKopecks, marginPct }) => (
  <div className="flex flex-col items-end gap-1.5">
    <span className="text-[16px] font-medium leading-[22px] tracking-[-0.234px] text-[#161922] whitespace-nowrap">
      {kopToRubSmart(amount)} ₽
    </span>
    {mode === 'usage'
      ? units !== undefined && (
          <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286] whitespace-nowrap">
            {formatUnitsCompact(units)} шт. ({formatPricePerUnit(tariffKopecks ?? 0)})
          </span>
        )
      : marginPct != null && (
          <span
            className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] whitespace-nowrap"
            style={{ color: marginPct < 0 ? '#DF1C41' : '#16a34a' }}
          >
            маржа {formatMarginPct(marginPct)}
          </span>
        )}
  </div>
);

// ─── BalanceCell — баланс + расходы клиента и маржа за период (REL-246/248) ───

const BalanceCell: React.FC<{
  balanceKopecks: number;
  /** REL-246: общая сумма расходов клиента за период (Σ списаний по модулям), а не дельта баланса. */
  expensesKopecks?: number;
  /** REL-248: наша маржа в % = 100·(расходы клиента − наши траты)/расходы клиента. */
  marginPct?: number | null;
}> = ({ balanceKopecks, expensesKopecks, marginPct }) => (
  <div className="flex flex-col items-end gap-1.5">
    <span className="text-[16px] font-medium leading-[22px] tracking-[-0.234px] text-[#161922] whitespace-nowrap">
      {kopToRubSmart(balanceKopecks)} ₽
    </span>
    {expensesKopecks != null && (
      <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286] whitespace-nowrap">
        {kopToRubSmart(expensesKopecks)} ₽
        {marginPct != null && ` (${formatMarginPct(marginPct)})`}
      </span>
    )}
  </div>
);

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

interface DeleteModalProps {
  company: Company;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

// REL-57: модалка подтверждения удаления компании по макету (заголовок слева + ×, серый текст,
// кнопки справа «Отмена» / «Удалить компанию»).
const DeleteConfirmModal: React.FC<DeleteModalProps> = ({ company, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl shadow-xl max-w-[520px] w-full mx-4 p-6">
      {/* Заголовок + × */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="text-[18px] font-semibold text-[#0a0d14] leading-snug">
          Вы действительно хотите удалить компанию?
        </h3>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Закрыть"
          className="text-[#868c98] hover:text-[#0a0d14] text-2xl leading-none shrink-0"
        >
          ×
        </button>
      </div>

      {/* Текст */}
      <p className="text-[13px] text-[#868c98] mb-6 leading-relaxed">
        Вы собираетесь безвозвратно удалить компанию{' '}
        <span className="text-[#525866] font-medium">«{company.name}»</span>. Это действие нельзя
        отменить.
      </p>

      {/* Кнопки справа */}
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-[10px] bg-[#f0f0f0] text-[#525866] hover:bg-[#e8e8e8] transition-colors font-medium text-[13px]"
        >
          Отмена
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-5 py-2.5 rounded-[10px] bg-[#e16349] text-white hover:bg-[#c8513a] transition-colors font-medium text-[13px] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Удаление...' : 'Удалить компанию'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

interface CompaniesManagementContentProps {
  /** Сообщает родителю (TeamManagementPage), открыта ли карточка компании — чтобы спрятать
      левое меню и отдать карточке всю ширину под полноширинный макет. */
  onDetailOpenChange?: (open: boolean) => void;
  /** REL-223: клик по названию компании → раздел «Статистика и лимиты» по этой компании. */
  onOpenCompanyLimits?: (companyId: number) => void;
}

export const CompaniesManagementContent: React.FC<CompaniesManagementContentProps> = ({
  onDetailOpenChange,
  onOpenCompanyLimits,
}) => {
  const colors = useBrandingColors();

  // Infinite scroll state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState(0);
  const PAGE_SIZE = 20;
  // Ref для sentinel-элемента внизу списка (IntersectionObserver)
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Ref на внутренний скролл-контейнер строк — root для IntersectionObserver (скролл только внутри таблицы)
  const scrollRef = useRef<HTMLDivElement>(null);

  // Фильтры
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  // Тосты скачивания отчёта (REL-195)
  const [toasts, setToasts] = useState<{ id: number; text: string }[]>([]);
  const pushToast = useCallback((text: string) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, text }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTab>('usage');

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editCompany, setEditCompany] = useState<Company | null>(null);
  const [topUpCompany, setTopUpCompany] = useState<Company | null>(null);
  const [detailCompany, setDetailCompany] = useState<Company | null>(null);
  // Уведомляем родителя об открытии/закрытии карточки (для полной ширины) + сброс при размонтировании
  useEffect(() => {
    onDetailOpenChange?.(detailCompany !== null);
  }, [detailCompany, onDetailOpenChange]);
  useEffect(() => () => onDetailOpenChange?.(false), [onDetailOpenChange]);
  const [deleteCompany, setDeleteCompany] = useState<Company | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Аналитика по ИИ-модулям на каждую компанию (тариф/себестоимость/выручка/прибыль) —
  // источник для стоимости модуля (REL-247), расходов клиента и маржи под балансом
  // (REL-246/248) и режима «Доход». Догружается отдельным endpoint'ом, как и список.
  // null — попытка загрузки завершилась ошибкой (помечаем, чтобы не ретраить бесконечно).
  const [analyticsMap, setAnalyticsMap] = useState<Record<number, CompanyModuleAnalytics | null>>({});

  // Суммарные показатели по ВСЕМ компаниям текущего фильтра (не по загруженной странице):
  // считаем отдельно от бесконечного скролла, чтобы виджеты показывали полный итог сразу,
  // а не «росли» по мере подгрузки. null — пока считается (виджеты используют сумму по загруженным).
  const [totals, setTotals] = useState<{ balanceKopecks: number; turnoverKopecks: number } | null>(null);

  // Счётчик принудительной перезагрузки (REL-257). Бампается в reload() после любой мутации
  // (редактирование/пополнение/удаление компании) и входит в зависимости эффекта итогов —
  // иначе виджеты «баланс компаний»/«оборот ИИ» считают только при смене фильтров и остаются
  // со старыми цифрами до F5, рассинхронизируясь с обновившейся таблицей.
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Debounce search — сбрасываем список при изменении поискового запроса.
  // Первый (mount) прогон пропускаем: начальную загрузку делает loadPage, а отложенный на 350мс
  // setCompanies([]) гонялся с ней и затирал уже загруженный список (при быстром ответе бэкенда
  // < 350мс таблица оставалась пустой — "Компании не найдены").
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setCompanies([]);
      setAnalyticsMap({});
      setNextPage(0);
      setHasMore(false);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Сброс при смене статуса/периода
  useEffect(() => {
    setCompanies([]);
    setAnalyticsMap({});
    setNextPage(0);
    setHasMore(false);
  }, [statusFilter, dateFrom, dateTo]);

  // Загрузка страницы (первой или следующей)
  const loadPage = useCallback(async (page: number, reset: boolean) => {
    if (page === 0) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const res = await companiesManagementService.getCompanies({
        search: debouncedSearch || undefined,
        status: statusFilter.length ? statusFilter.join(',') : undefined,
        page,
        size: PAGE_SIZE,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        includeStats: true,
      });
      const newItems = res.content ?? [];
      const total = Number(res.totalPages ?? 1);
      setCompanies(prev => reset ? newItems : [...prev, ...newItems]);
      setNextPage(page + 1);
      setHasMore(page + 1 < total);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, statusFilter, dateFrom, dateTo]);

  // Начальная загрузка / перезагрузка при смене фильтров
  useEffect(() => {
    loadPage(0, true);
  }, [loadPage]);

  // Итоги по всем компаниям текущего фильтра (для виджетов «баланс компаний» / «оборот ИИ»):
  // догружаем все страницы один раз и суммируем, чтобы итог был полным сразу, а не рос при скролле.
  useEffect(() => {
    let cancelled = false;
    setTotals(null);
    (async () => {
      const TOTALS_PAGE_SIZE = 100;
      let balanceKopecks = 0;
      let turnoverKopecks = 0;
      let page = 0;
      let totalPages = 1;
      try {
        do {
          const res = await companiesManagementService.getCompanies({
            search: debouncedSearch || undefined,
            status: statusFilter.length ? statusFilter.join(',') : undefined,
            page,
            size: TOTALS_PAGE_SIZE,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            includeStats: true,
          });
          const items = res.content ?? [];
          balanceKopecks += companiesManagementService.getTotalBalanceKopecks(items);
          turnoverKopecks += companiesManagementService.getTotalAiTurnoverKopecks(items);
          totalPages = Number(res.totalPages ?? 1);
          page += 1;
        } while (page < totalPages && !cancelled);
        if (!cancelled) setTotals({ balanceKopecks, turnoverKopecks });
      } catch { /* виджеты покажут сумму по загруженным как фолбэк */ }
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, statusFilter, dateFrom, dateTo, refreshNonce]);

  // IntersectionObserver для infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadPage(nextPage, false);
      }
    }, { root: scrollRef.current, rootMargin: '120px', threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, nextPage, loadPage]);

  // Догружаем аналитику по модулям для новых компаний (тариф/себестоимость/выручка за период).
  // Early-return при отсутствии недостающих делает зависимость от analyticsMap безопасной (без цикла).
  useEffect(() => {
    const missing = companies.filter(c => c.id != null && analyticsMap[c.id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map(async c => {
        try {
          const a = await companiesManagementService.getModuleAnalytics(
            c.id!,
            dateFrom || undefined,
            dateTo || undefined,
          );
          return [c.id!, a] as const;
        } catch {
          // Помечаем компанию null (загрузка не удалась) — иначе она остаётся «missing»
          // и эффект перезапускается бесконечно (analyticsMap меняет ссылку каждый раз).
          return [c.id!, null] as const;
        }
      }),
    ).then(results => {
      if (cancelled) return;
      setAnalyticsMap(prev => {
        const next = { ...prev };
        for (const [id, a] of results) next[id] = a;
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [companies, analyticsMap, dateFrom, dateTo]);

  // Перезагрузка списка с начала (после мутаций). Кроме строк/аналитики таблицы бампаем
  // refreshNonce, чтобы пересчитались и виджеты-итоги (их эффект иначе реагирует только на
  // смену фильтров) — все поверхности экрана обновляются разом, без F5 (REL-257).
  const reload = useCallback(() => {
    setCompanies([]);
    setAnalyticsMap({});
    setNextPage(0);
    setHasMore(false);
    setRefreshNonce(n => n + 1);
    loadPage(0, true);
  }, [loadPage]);

  // Суммарные метрики для виджетов: полный итог по всем компаниям фильтра (totals),
  // пока он считается — фолбэк на сумму по уже загруженным строкам.
  const totalBalanceKopecks = totals?.balanceKopecks ?? companiesManagementService.getTotalBalanceKopecks(companies);
  const totalTurnoverKopecks = totals?.turnoverKopecks ?? companiesManagementService.getTotalAiTurnoverKopecks(companies);

  // Скачивание отчёта по всем компаниям (REL-195): тосты «Подготовка…» → «Скачан».
  const handleDownloadReport = async () => {
    // Экспорт по ВСЕМ компаниям текущего фильтра, а не только по загруженной странице:
    // догружаем все страницы перед формированием файла (P2 ревью).
    pushToast('Подготовка отчёта по всем компаниям...');
    try {
      const EXPORT_PAGE_SIZE = 100;
      const all: Company[] = [];
      let page = 0;
      let totalPages = 1;
      do {
        const res = await companiesManagementService.getCompanies({
          search: debouncedSearch || undefined,
          status: statusFilter.length ? statusFilter.join(',') : undefined,
          page,
          size: EXPORT_PAGE_SIZE,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          includeStats: false,
        });
        all.push(...(res.content ?? []));
        totalPages = Number(res.totalPages ?? 1);
        page += 1;
      } while (page < totalPages);

      const rows = all.map(c => ({
        name: c.name,
        ownerEmail: c.ownerEmail ?? '',
        status: c.status,
        balanceRub: (c.balanceKopecks ?? 0) / 100,
        accountsCount: c.accountsCount ?? 0,
        maxAccounts: c.maxAccounts ?? null,
      }));
      const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'companies-report.json';
      a.click();
      URL.revokeObjectURL(url);
      pushToast(`Отчёт успешно скачан! (${rows.length})`);
    } catch {
      pushToast('Не удалось сформировать отчёт');
    }
  };

  const handleDelete = async () => {
    if (!deleteCompany) return;
    setDeleteLoading(true);
    try {
      await companiesManagementService.deleteCompany(deleteCompany.id!);
      setDeleteCompany(null);
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Ошибка удаления');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Карточка одной компании (вид суперадмина) — заменяет список, «Назад» возвращает.
  if (detailCompany) {
    return (
      <CompanyCardContent company={detailCompany} onBack={() => setDetailCompany(null)} />
    );
  }

  return (
    <div className="bg-[#f5f6f1] rounded-[24px] w-full h-full overflow-hidden flex flex-col gap-6 p-6">

      {/* Stat Widgets */}
      <div className="flex gap-4 shrink-0">
        <StatWidget
          icon={<BalanceIcon />}
          label="баланс компаний"
          kopecks={totalBalanceKopecks}
        />
        <StatWidget
          icon={<ChartIcon />}
          label="Суммарный оборот ИИ"
          kopecks={totalTurnoverKopecks}
        />
      </div>

      {/* Разделитель под виджетами (#EBE7DC, отступ по бокам 20px) */}
      <div className="px-5 shrink-0">
        <div className="h-px bg-[#EBE7DC]" />
      </div>

      {/* Единый блок фильтров и действий: слева переключатель+поиск, справа кнопки.
          flex-wrap + min-width слева — на узкой панели (рядом меню «Управление», ~786px)
          правый блок переносится на вторую строку, а поиск не схлопывается в 0 и не перекрывается. */}
      <div className="flex items-center gap-4 flex-wrap gap-y-3 shrink-0">
        {/* Левая половина: сегментированный переключатель + поиск */}
        <div className="flex items-center gap-2 flex-1 min-w-[300px]">
          {/* View Toggle [1.0] — сегментированный переключатель */}
          <div className="flex items-center gap-1 p-0.5 bg-black/5 rounded-[12px] shrink-0">
            {(['usage', 'revenue'] as ActiveTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 rounded-[10px] text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors ${
                  activeTab === tab
                    ? 'bg-white text-[#56584F] shadow-[0px_6px_10px_rgba(27,28,29,0.06),0px_2px_4px_rgba(27,28,29,0.02)]'
                    : 'text-[#868C98]'
                }`}
              >
                {tab === 'usage' ? 'Применение' : 'Доход'}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 bg-white border border-[#EBE7DC] rounded-[10px] px-2 py-2.5 flex-1 min-w-0">
            <SearchIcon />
            <input
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-[14px] leading-5 tracking-[-0.006em] text-[#525866] placeholder:text-[#525866] outline-none"
            />
          </div>
        </div>

        {/* Правая половина: фильтр статусов, период, отчёт, добавить */}
        <div className="flex items-center justify-end gap-2 shrink-0">
          {/* Status Filter — мультиселект (REL-195) */}
          <StatusMultiSelect selected={statusFilter} onChange={setStatusFilter} />

          {/* Period Picker */}
          <PeriodPicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
          />

          {/* Download — отчёт по всем компаниям с тостами (REL-195) */}
          <button
            onClick={handleDownloadReport}
            title="Скачать отчёт"
            className="flex items-center justify-center w-10 h-10 bg-white rounded-[10px] text-[#525866] shadow-[0px_1px_2px_rgba(55,93,251,0.08)] hover:bg-[#f8f9fa] transition-colors shrink-0"
          >
            <DownloadIcon />
          </button>

          {/* Create Button — оранжевая (#E16349 по макету) */}
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1 px-2.5 py-2.5 rounded-[10px] text-white text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors whitespace-nowrap"
            style={getButtonInlineStyles(colors, 'primary')}
            onMouseEnter={e => Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary'))}
            onMouseLeave={e => Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary'))}
          >
            <PlusIcon /> Добавить компанию
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-[12px] px-4 py-3 text-[13px] text-red-600">
          {error}
        </div>
      )}

      {/* Table — flex-1 min-h-0 + внутренняя flex-колонка: таблица занимает остаток высоты панели,
          шапка фиксирована (shrink-0), а прокручивается только область строк (scrollRef) —
          виджеты/тулбар/шапка остаются на месте (REL-228). */}
      <div className="bg-white rounded-[16px] border border-[#F4F5F0] overflow-hidden flex-1 min-h-0 flex flex-col">
        {/* Header — белая, текст #6B7280 14px, числовые колонки выровнены вправо; фиксирована (shrink-0) */}
        <div className="flex items-center h-[52px] bg-white border-b border-[#F4F5F0] text-[14px] font-medium leading-[1.3] text-[#6B7280] shrink-0">
          <div className="flex-[1.6] min-w-[230px] pl-[13px] pr-3">О компании</div>
          <div className="flex-1 min-w-[120px] px-6 text-right">Баланс</div>
          <div className="flex-1 min-w-[120px] px-6 text-right">Скоринг резюме</div>
          <div className="flex-1 min-w-[120px] px-6 text-right">Диалоги с ИИ</div>
          <div className="flex-1 min-w-[120px] px-6 text-right">ИИ-Скрининг</div>
          <div className="w-11 shrink-0 border-l border-[#F4F5F0]" />
        </div>

        {/* Прокручивается только эта область (строки); шапка/виджеты/тулбар остаются на месте */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {/* Rows */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: colors.primary }}
            />
          </div>
        ) : companies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[#868c98]">
            <span className="text-[13px]">Компании не найдены</span>
          </div>
        ) : (
          companies.map(company => {
            const s = company.stats;
            // Аналитика по модулям компании (тариф/себестоимость/выручка) — для стоимости модуля,
            // расходов клиента и маржи. null, пока догружается.
            const analytics = company.id != null ? (analyticsMap[company.id] ?? null) : null;
            const moduleOf = (code: string) =>
              analytics?.modules.find(m => m.module === code) ?? null;
            const searchM = moduleOf('SEARCH');
            const dialogM = moduleOf('DIALOG');
            const interviewM = moduleOf('INTERVIEW');
            // Маржа модуля (Доход) и общая маржа (под балансом, REL-248); null при нулевой выручке.
            const moduleMargin = (m: typeof searchM) =>
              m && m.revenueKopecks > 0 ? marginPctOf(m.revenueKopecks, m.costKopecks) : null;
            const totalMargin =
              analytics && analytics.revenueKopecks > 0
                ? marginPctOf(analytics.revenueKopecks, analytics.costKopecks)
                : null;
            // REL-246: расходы клиента = totalSpentKopecks; если поле не пришло — суммируем
            // расход по модулям (тождественно totalSpent), чтобы строка не пропадала.
            const expensesKopecks = s
              ? s.totalSpentKopecks ??
                (s.searchKopecks ?? 0) + (s.dialogKopecks ?? 0) + (s.interviewKopecks ?? 0)
              : undefined;
            const isBlocked = company.status === 'INACTIVE';
            // REL-222: под названием компании — одна дата (не диапазон). PILOT → дата старта
            // пилота; любой другой статус (Активная/На паузе/Заблокированная) → дата начала
            // подписки. Текст статуса остаётся только когда подходящей даты нет.
            const lineDateIso =
              company.status === 'PILOT'
                ? company.pilotStartDate
                : company.subscriptionStartDate;
            const secondLine = lineDateIso
              ? formatRuDate(lineDateIso)
              : isBlocked
                ? 'Заблокированные'
                : company.status === 'SUSPENDED'
                  ? 'На паузе'
                  : (STATUS_LABELS[company.status] ?? company.status);
            return (
              <div
                key={company.id}
                className="group flex items-stretch min-h-[72px] border-b border-[#F4F5F0] last:border-b-0 hover:bg-[#fafafa] transition-colors"
              >
                {/* О компании: чекбокс + (имя/счётчик / статус+дата) */}
                <div className="flex-[1.6] min-w-[230px] flex items-start gap-3 py-3 pl-3 pr-6">
                  {/* Чекбокс по дизайн-системе: 24px, серая рамка #E2E4E9 (инсет 10%, r4) + белый центр (инсет 17.5%, r2.6) с тенью */}
                  <label className="relative shrink-0 w-6 h-6 mt-px cursor-pointer block">
                    <input type="checkbox" className="peer sr-only" />
                    <span className="absolute inset-[10%] rounded-[4px] bg-[#E2E4E9] peer-checked:bg-[#e16349]" />
                    <span className="absolute inset-[17.5%] rounded-[2.6px] bg-white shadow-[0px_2px_2px_rgba(27,28,29,0.12)] peer-checked:hidden" />
                  </label>
                  <div className="min-w-0 flex flex-col gap-1">
                    {/* Имя + счётчик аккаунтов */}
                    <div className="flex items-center gap-2">
                      {/* REL-223: клик по имени → раздел «Статистика и лимиты» по этой компании.
                          Подсветка поиска (по макету): брендовым #E16349 красится ТОЛЬКО совпавшая
                          подстрока запроса в имени, остальное — обычным цветом (см. highlightMatch). */}
                      <button
                        type="button"
                        onClick={() => company.id != null && onOpenCompanyLimits?.(company.id)}
                        title="Открыть статистику и лимиты компании"
                        className="text-[16px] font-medium leading-[22px] tracking-[-0.234px] truncate hover:underline transition-colors text-left text-[#161922] hover:text-[#e16349]"
                      >
                        {highlightMatch(company.name, debouncedSearch)}
                      </button>
                      {company.accountsCount !== undefined && (
                        <span
                          className="flex items-center gap-0.5 shrink-0"
                          title="Фактически созданных аккаунтов рекрутеров"
                        >
                          <UserCountIcon />
                          <span className="text-[15px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286]">
                            {company.accountsCount}
                          </span>
                        </span>
                      )}
                    </div>
                    {/* Статус-точка + диапазон дат (заблокированные — красным) */}
                    <div className="flex items-center gap-1">
                      <StatusDot status={company.status} />
                      <span
                        className="text-[14px] leading-5 tracking-[-0.076px] truncate"
                        style={{ color: isBlocked && !lineDateIso ? STATUS_COLORS.INACTIVE : '#989286' }}
                      >
                        {secondLine}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Баланс / Прогноз */}
                <div className="flex-1 min-w-[120px] flex flex-col justify-center items-end py-3 px-6">
                  <BalanceCell
                    balanceKopecks={company.balanceKopecks ?? 0}
                    expensesKopecks={expensesKopecks}
                    marginPct={totalMargin}
                  />
                </div>

                {/* Скоринг */}
                <div className="flex-1 min-w-[120px] flex flex-col justify-center items-end py-3 px-6">
                  {s ? (
                    <StatsCell
                      amount={s.searchKopecks}
                      mode={activeTab}
                      units={s.searchUnits}
                      tariffKopecks={searchM?.tariffKopecks ?? 0}
                      marginPct={moduleMargin(searchM)}
                    />
                  ) : <span className="text-[14px] text-[#989286]">—</span>}
                </div>

                {/* Диалоги */}
                <div className="flex-1 min-w-[120px] flex flex-col justify-center items-end py-3 px-6">
                  {s ? (
                    <StatsCell
                      amount={s.dialogKopecks}
                      mode={activeTab}
                      units={s.dialogUnits}
                      tariffKopecks={dialogM?.tariffKopecks ?? 0}
                      marginPct={moduleMargin(dialogM)}
                    />
                  ) : <span className="text-[14px] text-[#989286]">—</span>}
                </div>

                {/* ИИ-Скрининг */}
                <div className="flex-1 min-w-[120px] flex flex-col justify-center items-end py-3 px-6">
                  {s ? (
                    <StatsCell
                      amount={s.interviewKopecks}
                      mode={activeTab}
                      units={s.interviewUnits}
                      tariffKopecks={interviewM?.tariffKopecks ?? 0}
                      marginPct={moduleMargin(interviewM)}
                    />
                  ) : <span className="text-[14px] text-[#989286]">—</span>}
                </div>

                {/* Действия: контекстное меню (⋮) — раскрывает ⊕ / 📊 / ✏️ / 🗑️ */}
                {/* Колонка действий остаётся белой даже при ховере строки (по макету) */}
                <div className="w-11 shrink-0 flex items-start justify-center py-3 border-l border-[#F4F5F0] relative bg-white">
                  <RowActions
                    onTopUp={() => setTopUpCompany(company)}
                    onStats={() => setDetailCompany(company)}
                    onEdit={() => setEditCompany(company)}
                    onDelete={() => setDeleteCompany(company)}
                  />
                </div>
              </div>
            );
          })
        )}
          {/* Infinite scroll sentinel — внутри скролл-контейнера строк */}
          <div ref={sentinelRef} className="h-4" />
          {loadingMore && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: colors.primary }} />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {createOpen && (
        <CreateCompanyModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); reload(); }}
        />
      )}

      {editCompany && (
        <EditCompanyModal
          company={editCompany}
          onClose={() => setEditCompany(null)}
          onSaved={() => { setEditCompany(null); reload(); }}
        />
      )}

      {topUpCompany && (
        <TopUpBalanceModal
          company={topUpCompany}
          onClose={() => setTopUpCompany(null)}
          onSuccess={() => { setTopUpCompany(null); reload(); }}
        />
      )}


      {/* Тосты скачивания отчёта (REL-195) */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-2">
          {toasts.map(t => (
            <div
              key={t.id}
              className="flex items-center gap-2 bg-white border border-[#e2e4e9] rounded-[10px] shadow-lg px-4 py-2.5 text-[13px] text-[#0a0d14]"
            >
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#16a34a] text-white text-[10px]">
                ✓
              </span>
              {t.text}
            </div>
          ))}
        </div>
      )}

      {deleteCompany && (
        <DeleteConfirmModal
          company={deleteCompany}
          onConfirm={handleDelete}
          onCancel={() => setDeleteCompany(null)}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default CompaniesManagementContent;
